import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Building, Plus, Trash2, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Alert, AlertDescription } from '@/components/ui/alert';

export interface BuildingData {
  name: string;
  address: string;
  apartment_count: number;
}

interface Step3BuildingsProps {
  buildings: BuildingData[];
  onChange: (buildings: BuildingData[]) => void;
}

export const Step3Buildings: React.FC<Step3BuildingsProps> = ({ buildings, onChange }) => {
  const { toast } = useToast();

  const checkDuplicateName = (name: string, currentIndex: number): boolean => {
    const trimmedName = name.trim().toLowerCase();
    if (!trimmedName) return false;
    
    return buildings.some((b, i) => 
      i !== currentIndex && 
      b.name.trim().toLowerCase() === trimmedName
    );
  };

  const addBuilding = () => {
    onChange([...buildings, { name: '', address: '', apartment_count: 0 }]);
  };

  const removeBuilding = (index: number) => {
    onChange(buildings.filter((_, i) => i !== index));
  };

  const updateBuilding = (index: number, field: keyof BuildingData, value: string | number) => {
    // Check for duplicate building name
    if (field === 'name' && typeof value === 'string') {
      if (checkDuplicateName(value, index)) {
        toast({
          title: "Duplicate Building Name",
          description: "A building with this name already exists. Please use a unique name.",
          variant: "destructive",
        });
        return; // Don't update if duplicate
      }
    }

    const updated = [...buildings];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  // Check if there are any duplicates
  const hasDuplicates = buildings.some((building, i) => 
    building.name && checkDuplicateName(building.name, i)
  );

  return (
    <div className="space-y-6">
      {hasDuplicates && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            You have duplicate building names. Each building must have a unique name.
          </AlertDescription>
        </Alert>
      )}
      
      <div className="flex items-center gap-3 mb-6">
        <Building className="h-8 w-8 text-primary" />
        <div>
          <h2 className="text-2xl font-bold text-foreground">Building Configuration</h2>
          <p className="text-muted-foreground">Add buildings that will be managed by your organization</p>
        </div>
      </div>

      <div className="space-y-4">
        {buildings.map((building, index) => (
          <div key={index} className="border rounded-lg p-4 bg-card space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold">Building {index + 1}</h3>
              {buildings.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeBuilding(index)}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor={`building-name-${index}`}>Building Name *</Label>
                <Input
                  id={`building-name-${index}`}
                  value={building.name}
                  onChange={(e) => updateBuilding(index, 'name', e.target.value)}
                  placeholder="e.g., Building A"
                  required
                  className={checkDuplicateName(building.name, index) ? 'border-destructive' : ''}
                />
                {checkDuplicateName(building.name, index) && (
                  <p className="text-xs text-destructive">Duplicate name</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor={`building-address-${index}`}>Address</Label>
                <Input
                  id={`building-address-${index}`}
                  value={building.address}
                  onChange={(e) => updateBuilding(index, 'address', e.target.value)}
                  placeholder="Building address"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor={`building-apartments-${index}`}>Apartment Count *</Label>
                <Input
                  id={`building-apartments-${index}`}
                  type="number"
                  min="1"
                  value={building.apartment_count || ''}
                  onChange={(e) => updateBuilding(index, 'apartment_count', parseInt(e.target.value) || 0)}
                  placeholder="0"
                  required
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <Button
        type="button"
        variant="outline"
        onClick={addBuilding}
        className="w-full"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add Another Building
      </Button>

      {buildings.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-4">
          Add at least one building to continue
        </p>
      )}
    </div>
  );
};
